import React, {useCallback, useMemo} from 'react';
import {useSession} from '../../../firebase/UserProvider';
import {useIcrCoverage} from '../coverage/useIcrCoverage';
import {
    buildCollectionCoverageView,
    buildCollectionEeiSegments,
} from '../coverage/buildCollectionCoverageView';
import useCoverageModalUrlState from '../coverage/useCoverageModalUrlState';
import {COVERAGE_SECTIONS} from '../coverage/coverageModalUrlState';
import CollectionCoverageModal from '../coverage/collectionCoverage/CollectionCoverageModal';
import AnalyticCoverageModal from '../coverage/analytic/AnalyticCoverageModal';
import useCollectionMaterialViewer from '../../collection/viewer/useCollectionMaterialViewer';
import {resolvePirEeis} from '../pirPayload';

/**
 * Renders the coverage modals for /icr/:id. Visual cards have been moved to
 * IcrCoverageTab (Coverage snapshot header). This component is kept to manage
 * modal state and data fetching so the modals remain functional from any tab.
 */
const IcrCoveragePanel = ({icrId, icrNumber, subject, coverage: coverageProp = null, pir = null}) => {
    const {role, roles} = useSession();
    const isAdmin = role === 'admin' || (roles || []).includes('admin');
    const canRecomputeCoverage = isAdmin
        || role === 'collection_manager'
        || (roles || []).includes('collection_manager');
    const controlled = coverageProp != null;
    const internal = useIcrCoverage(controlled ? null : icrId);
    const data = controlled ? coverageProp.data : internal.data;
    const awaitingAnalytic = controlled
        ? Boolean(coverageProp.awaitingAnalytic)
        : Boolean(internal.awaitingAnalytic);
    const awaitingCollection = controlled
        ? Boolean(coverageProp.awaitingCollection)
        : Boolean(internal.awaitingCollection);
    const enqueueInFlight = controlled
        ? Boolean(coverageProp.recomputing)
        : Boolean(internal.recomputing);
    const recompute = controlled ? coverageProp.recompute : internal.recompute;

    const {state: modalState, updateModal, closeModal, currentReturnTo} = useCoverageModalUrlState();
    const {openCollectionMaterial} = useCollectionMaterialViewer();

    const pirEeis = useMemo(() => resolvePirEeis(pir), [pir]);
    const collectionView = useMemo(() => buildCollectionCoverageView(data), [data]);
    const collectionSegments = useMemo(
        () => buildCollectionEeiSegments(collectionView, pirEeis),
        [collectionView, pirEeis],
    );

    const showCollectionComputing = Boolean(collectionView?.computing || data?.collectionCoverage?.computing)
        || awaitingCollection;
    const showAnalyticComputing = Boolean(
        data?.analyticCoverage?.computing ?? data?.computing,
    ) || awaitingAnalytic;

    const collectionBusy = enqueueInFlight || awaitingCollection || showCollectionComputing;
    const analyticBusy = enqueueInFlight || awaitingAnalytic || showAnalyticComputing;

    const handleOpenMaterial = useCallback((material) => {
        if (!material?.id) {
            return;
        }
        openCollectionMaterial({
            materialId: material.id,
            materialKind: material.materialKind === 'iir' ? 'iir' : 'cm',
            returnTo: currentReturnTo,
        });
    }, [openCollectionMaterial, currentReturnTo]);

    const handleRecomputeCollection = useCallback(() => {
        recompute?.({scope: 'collection'});
    }, [recompute]);

    const handleRecomputeAnalytic = useCallback(() => {
        recompute?.({scope: 'analytic'});
    }, [recompute]);

    if (!icrId) {
        return null;
    }

    return (
        <>
            <CollectionCoverageModal
                open={modalState?.section === COVERAGE_SECTIONS.COLLECTION}
                onClose={closeModal}
                snapshot={data}
                icrNumber={icrNumber}
                subject={subject}
                pirEeis={pirEeis}
                tab={modalState?.section === COVERAGE_SECTIONS.COLLECTION ? modalState.tab : undefined}
                expandedEeiId={modalState?.eeiId ?? null}
                onTabChange={(tab) => updateModal({section: COVERAGE_SECTIONS.COLLECTION, tab})}
                onToggleEei={(eeiId) => updateModal({
                    section: COVERAGE_SECTIONS.COLLECTION,
                    tab: modalState?.tab,
                    eeiId,
                })}
                onOpenMaterial={handleOpenMaterial}
                onRecompute={canRecomputeCoverage ? handleRecomputeCollection : undefined}
                recomputing={collectionBusy}
                canRecompute={canRecomputeCoverage}
            />

            <AnalyticCoverageModal
                open={modalState?.section === COVERAGE_SECTIONS.ANALYTIC}
                onClose={closeModal}
                snapshot={data}
                icrNumber={icrNumber}
                subject={subject}
                icrId={icrId}
                tab={modalState?.section === COVERAGE_SECTIONS.ANALYTIC ? modalState.tab : undefined}
                activeQuestionId={modalState?.questionId ?? null}
                activeConflictId={modalState?.conflictId ?? null}
                onTabChange={(tab) => updateModal({section: COVERAGE_SECTIONS.ANALYTIC, tab})}
                onNavigateToConflict={(conflictId) => updateModal({
                    section: COVERAGE_SECTIONS.ANALYTIC,
                    tab: 'conflicts',
                    conflictId,
                })}
                onNavigateToQuestion={(questionId) => updateModal({
                    section: COVERAGE_SECTIONS.ANALYTIC,
                    tab: 'questions',
                    questionId,
                })}
                onRecompute={canRecomputeCoverage ? handleRecomputeAnalytic : undefined}
                recomputing={analyticBusy}
                isAdmin={isAdmin}
                canRecompute={canRecomputeCoverage}
            />
        </>
    );
};

export default IcrCoveragePanel;
